import { type ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Circle,
  PartyPopper,
  Target,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { emissionsTrackerRoutes, rootAppPath } from '../../routes';
import { generateUrl } from '@routes/util';
import type { OnboardingItems } from '../../types/emissions';
import { METHOD_CARDS, DataMethod } from '../form/types';

// Look up the METHOD_CARDS entry (which owns the icon + branded colour) for a
// given DataMethod so checklist buttons stay visually consistent with the
// Method-selection step users see after clicking through.
const METHOD_CARD_BY_KEY = new Map(METHOD_CARDS.map((card) => [card.key, card]));

interface OnboardingChecklistProps {
  initiativeId: string;
  items: OnboardingItems;
  onOpenTargetModal: () => void;
  /** Opens the CreateActivityModal for the given method (asset / activity / bulk variants). */
  onOpenCreateFlow: (method: DataMethod) => void;
  onDismiss: () => void;
  isDismissing: boolean;
}

interface ChecklistAction {
  /** Override the default label derived from METHOD_CARDS (e.g. "Add Asset" instead of "By Asset"). */
  label: string;
  method: DataMethod;
}

interface ChecklistItem {
  id: keyof OnboardingItems;
  title: string;
  description: string;
  icon: ReactNode;
  /** Navigate on click — used for the base-year step (takes user to settings page). */
  to?: string;
  /** Inline click handler — used for the target step (opens SetTargetModal in-place). */
  onClick?: () => void;
  /**
   * One or two action buttons — used for rows that support multiple
   * entry paths (e.g. activity row's single-create + bulk file upload).
   * Mutually exclusive with `to` / `onClick`.
   */
  actions?: [ChecklistAction] | [ChecklistAction, ChecklistAction];
  /** Optional steps don't block completion — user can skip them and still reach the celebration state. */
  optional?: boolean;
}

export const OnboardingChecklist = ({
  initiativeId,
  items,
  onOpenTargetModal,
  onOpenCreateFlow,
  onDismiss,
  isDismissing,
}: OnboardingChecklistProps) => {
  const checklistItems: ChecklistItem[] = [
    {
      id: 'hasBaseYear',
      title: 'Set base year',
      description: 'Pick the reference year for tracking emissions reduction progress.',
      icon: <Calendar className='tw:size-4' />,
      to: generateUrl(emissionsTrackerRoutes.EMISSIONS_SETTINGS, { rootAppPath, initiativeId }),
    },
    {
      id: 'hasAssets',
      title: 'Add your first asset',
      description: 'Register a facility, fleet, data centre, or other emission source. Optional \u2014 you can attribute activities to assets later.',
      // Row icon is unused when `actions` is set — the action buttons carry their own branded icons.
      icon: null,
      actions: [
        // Single + bulk asset paths are unified inside the wizard now;
        // the "Add Asset" tile drops both into the same flow.
        { label: 'Add Asset', method: DataMethod.ByAsset },
      ],
      optional: true,
    },
    {
      id: 'hasActivities',
      title: 'Add your first activity',
      description: 'Record an emission activity (fuel, electricity, travel, etc.).',
      icon: null,
      actions: [
        { label: 'Add Activity', method: DataMethod.ByActivity },
        { label: 'Bulk Upload', method: DataMethod.BulkUpload },
      ],
    },
    {
      id: 'hasTarget',
      title: 'Set an emission reduction target',
      description: 'Define a reduction goal to track progress against a framework (SBTi, Net Zero, etc.).',
      icon: <Target className='tw:size-4' />,
      onClick: onOpenTargetModal,
    },
  ];

  // Progress counts all items (required + optional) so users see credit for asset setup.
  // Completion only requires the required items — optional items (assets) never block the
  // celebration state, so users who choose the Activities-only path can still finish.
  const completedCount = checklistItems.filter((item) => items[item.id]).length;
  const totalCount = checklistItems.length;
  const progressValue = (completedCount / totalCount) * 100;

  const requiredItems = checklistItems.filter((item) => !item.optional);
  const isRequiredComplete = requiredItems.every((item) => items[item.id]);

  if (isRequiredComplete) {
    return <CelebrationCard onDismiss={onDismiss} isDismissing={isDismissing} />;
  }

  // Highlight the next required step. Optional steps never become "primary" so the user
  // is always nudged toward what actually blocks completion.
  const firstIncompleteIndex = checklistItems.findIndex(
    (item) => !item.optional && !items[item.id]
  );

  return (
    <Card className='tw:max-w-3xl tw:mx-auto'>
      <CardHeader>
        <CardTitle>Get started with Emissions Tracker</CardTitle>
        <CardDescription>
          {completedCount} of {totalCount} steps complete
        </CardDescription>
        <Progress
          value={progressValue}
          indicatorClassName='tw:bg-emerald-600'
          className='tw:mt-2'
        />
      </CardHeader>
      <CardContent>
        <ul className='tw:flex tw:flex-col tw:gap-3'>
          {checklistItems.map((item, index) => (
            <ChecklistRow
              key={item.id}
              item={item}
              isComplete={items[item.id]}
              isPrimary={index === firstIncompleteIndex}
              onOpenCreateFlow={onOpenCreateFlow}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

interface ChecklistRowProps {
  item: ChecklistItem;
  isComplete: boolean;
  isPrimary: boolean;
  onOpenCreateFlow: (method: DataMethod) => void;
}

const ChecklistRow = ({ item, isComplete, isPrimary, onOpenCreateFlow }: ChecklistRowProps) => {
  const history = useHistory();
  const primaryVariant = isPrimary ? 'emerald' : 'outline';
  const buttonSize = 'sm';

  // Avoid the `<Button asChild><Link /></Button>` pattern — nesting an anchor
  // inside a Button breaks the button's styling (the Link's own styles cascade
  // in awkwardly). Use `history.push` from an onClick instead; same router
  // navigation, clean button rendering.
  const navigateTo = (to: string) => () => history.push(to);

  return (
    <li
      className={cn(
        'tw:flex tw:flex-col tw:gap-3 tw:p-3 tw:rounded-lg tw:border',
        isComplete ? 'tw:border-emerald-200 tw:bg-emerald-50/40' : 'tw:border-border'
      )}
    >
      <div className='tw:flex tw:items-start tw:gap-4'>
        <div className='tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center'>
          {isComplete ? (
            <CheckCircle2 className='tw:size-6 tw:text-emerald-600' />
          ) : (
            <Circle className='tw:size-6 tw:text-muted-foreground tw:opacity-60' />
          )}
        </div>

        <div className='tw:flex-1 tw:min-w-0'>
          <div className='tw:flex tw:items-center tw:gap-2'>
            <div
              className={cn(
                'tw:text-sm tw:font-medium',
                isComplete && 'tw:text-muted-foreground tw:line-through'
              )}
            >
              {item.title}
            </div>
            {item.optional && !isComplete ? (
              <Badge variant='secondary' className='tw:text-[10px] tw:font-normal'>
                Optional
              </Badge>
            ) : null}
          </div>
          <div className='tw:text-xs tw:text-muted-foreground'>{item.description}</div>
        </div>
      </div>

      {!isComplete ? (
        <div className='tw:flex tw:justify-end tw:gap-2'>
          {item.actions ? (
            item.actions.map((action) => (
              <MethodActionButton
                key={action.method}
                action={action}
                onClick={() => onOpenCreateFlow(action.method)}
              />
            ))
          ) : item.to ? (
            <Button variant={primaryVariant} size={buttonSize} onClick={navigateTo(item.to)}>
              {item.icon}
              Start
              <ArrowRight className='tw:size-3' />
            </Button>
          ) : (
            <Button variant={primaryVariant} size={buttonSize} onClick={item.onClick}>
              {item.icon}
              Start
              <ArrowRight className='tw:size-3' />
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
};

interface MethodActionButtonProps {
  action: ChecklistAction;
  onClick: () => void;
}

/**
 * Button for a single method entry (Add Activity, Bulk Asset, etc.). Pulls the
 * icon + colour from METHOD_CARDS so the checklist buttons stay visually
 * consistent with the Method step's card grid inside the form modal.
 */
const MethodActionButton = ({ action, onClick }: MethodActionButtonProps) => {
  const card = METHOD_CARD_BY_KEY.get(action.method);
  const Icon = card?.Icon;

  return (
    <Button variant='outline' size='sm' onClick={onClick}>
      {Icon && card ? (
        <span
          className={cn(
            'tw:mr-1.5 tw:inline-flex tw:size-5 tw:items-center tw:justify-center tw:rounded-full',
            card.iconBg
          )}
        >
          <Icon className={cn('tw:size-3', card.iconColor)} />
        </span>
      ) : null}
      {action.label}
    </Button>
  );
};

interface CelebrationCardProps {
  onDismiss: () => void;
  isDismissing: boolean;
}

const CelebrationCard = ({ onDismiss, isDismissing }: CelebrationCardProps) => {
  return (
    <Card className='tw:max-w-2xl tw:mx-auto tw:mt-8'>
      <CardContent className='tw:flex tw:flex-col tw:items-center tw:text-center tw:py-12 tw:px-8'>
        <div className='tw:mb-4 tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:bg-emerald-100 tw:text-emerald-700'>
          <PartyPopper className='tw:size-8' />
        </div>

        <h2 className='tw:text-2xl tw:font-semibold tw:mb-2'>You're all set!</h2>

        <p className='tw:text-sm tw:text-muted-foreground tw:max-w-md tw:mb-6'>
          Your Emissions Tracker is configured with a base year, at least one asset and activity,
          and a reduction target. View your dashboard to see insights from your data.
        </p>

        <Button variant='emerald' onClick={onDismiss} disabled={isDismissing}>
          View dashboard
          <ArrowRight className='tw:size-4' />
        </Button>
      </CardContent>
    </Card>
  );
};
